import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, UserRound, Home as HomeIcon, ShieldCheck } from "lucide-react";
import Logo from "../components/Logo";
import LanguageToggle from "../components/LanguageToggle";
import { useLang } from "../context/LanguageContext";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { Field } from "./Login";

export default function Register() {
  const { t } = useLang();
  const a = t.auth;
  const { register } = useAuth();
  const navigate = useNavigate();

  const [f, setF] = useState({
    firstName: "", lastName: "", phone: "", dob: "",
    street: "", number: "", postalCode: "", city: "", country: "",
    email: "", password: "", message: "",
  });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register({
        email: f.email,
        password: f.password,
        message: f.message,
        pid_identity: {
          first_name: f.firstName,
          last_name: f.lastName,
          phone: f.phone,
          date_of_birth: f.dob,
        },
        pid_address: {
          street: f.street,
          number: f.number,
          postal_code: f.postalCode,
          city: f.city,
          country: f.country,
        },
      });
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center px-5 py-16 overflow-hidden bg-u-bg">
      <div className="mesh-glow w-[500px] h-[500px] bg-u-purple/20 -top-32 -left-20" />
      <div className="mesh-glow w-[450px] h-[450px] bg-blue-400/20 bottom-0 right-0" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-2xl rounded-[32px] card p-8 sm:p-10"
      >
        <div className="flex items-center justify-between mb-8">
          <Logo size={36} />
          <LanguageToggle />
        </div>
        <h1 className="font-display font-bold text-3xl tracking-tight text-u-ink">{a.registerTitle}</h1>
        <p className="text-slate-500 mt-2">{a.registerSubtitle}</p>

        <form onSubmit={submit} className="mt-8 space-y-8" data-testid="register-form">
          {/* Identity */}
          <section>
            <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.15em] text-u-purple mb-4">
              <UserRound size={15} /> {a.identitySection}
            </h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label={a.firstName} type="text" value={f.firstName} onChange={set("firstName")} testid="register-firstname" />
              <Field label={a.lastName} type="text" value={f.lastName} onChange={set("lastName")} testid="register-lastname" />
              <Field label={a.phone} type="text" value={f.phone} onChange={set("phone")} testid="register-phone" optional />
              <Field label={a.dob} type="date" value={f.dob} onChange={set("dob")} testid="register-dob" optional />
            </div>
          </section>

          {/* Home address */}
          <section>
            <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.15em] text-u-purple mb-4">
              <HomeIcon size={15} /> {a.addressSection}
            </h2>
            <div className="grid sm:grid-cols-6 gap-5">
              <div className="sm:col-span-4"><Field label={a.street} type="text" value={f.street} onChange={set("street")} testid="register-street" /></div>
              <div className="sm:col-span-2"><Field label={a.number} type="text" value={f.number} onChange={set("number")} testid="register-number" /></div>
              <div className="sm:col-span-2"><Field label={a.postalCode} type="text" value={f.postalCode} onChange={set("postalCode")} testid="register-postalcode" /></div>
              <div className="sm:col-span-2"><Field label={a.city} type="text" value={f.city} onChange={set("city")} testid="register-city" /></div>
              <div className="sm:col-span-2"><Field label={a.country} type="text" value={f.country} onChange={set("country")} testid="register-country" /></div>
            </div>
          </section>

          {/* Account */}
          <section>
            <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.15em] text-u-purple mb-4">
              <ShieldCheck size={15} /> {a.accountSection}
            </h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label={a.email} type="email" value={f.email} onChange={set("email")} testid="register-email" />
              <Field label={a.password} type="password" value={f.password} onChange={set("password")} testid="register-password" />
            </div>
            <div className="mt-5">
              <label className="block text-sm font-medium text-slate-700 mb-2">{a.message}</label>
              <textarea
                rows={3} value={f.message} onChange={(e) => set("message")(e.target.value)}
                data-testid="register-message"
                className="w-full rounded-xl bg-u-bg border border-slate-200 px-4 py-3 text-u-ink placeholder-slate-400 outline-none focus:border-u-purple/60 focus:bg-white focus:ring-2 focus:ring-u-purple/15 transition-all resize-none"
              />
            </div>
          </section>

          {error && (
            <p data-testid="register-error" className="text-sm text-u-red bg-u-red/10 border border-u-red/20 rounded-xl px-4 py-3">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="register-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-u-ink px-6 py-3.5 font-semibold text-white hover:bg-u-ink/90 transition-all disabled:opacity-60"
          >
            {loading && <Loader2 size={18} className="animate-spin" />}
            {a.registerBtn}
          </button>
        </form>

        <p className="mt-7 text-center text-sm text-slate-500">
          {a.haveAccount}{" "}
          <a href="https://use1id-prd.vercel.app/r/logon" data-testid="goto-login" className="text-u-ink font-semibold hover:text-u-purple transition-colors">
            {a.signIn}
          </a>
        </p>
      </motion.div>
    </div>
  );
}
